import { ReframeVideoModal } from '@src/components/stage/ReframeVideoModal';
import { useTimelineTracks } from '@src/hooks/timeline/useTimelineTracks';
import { TrackLayerService } from '@src/services/timeline/tracks/TrackLayerService';
import {
  useEditor,
  useLayoutStore,
  useUserIdentity,
  useVideoSource,
} from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CutIcon from '@src/svgs/CutIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import TrackDownIcon from '@src/svgs/TrackDownIcon';
import TrackUpIcon from '@src/svgs/TrackUpIcon';
import ZoomMinusIcon from '@src/svgs/ZoomMinusIcon';
import ZoomPlusIcon from '@src/svgs/ZoomPlusIcon';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { throttle } from 'lodash';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { getDocumentHeight } from '../../../utility/general';
import PlainSlider from '../../common/PlainSlider';
import { VerticalSeparator } from '../../common/VerticalSeparator';
import RippleModeDropdown from './RippleModeDropdown';

import type { Result } from '@src/services/common/Result';
import type { DeleteResultSelectionTarget } from '@src/services/timeline/editing/DeleteActiveElementService';
import type { SplitResultSelectionTarget } from '@src/services/timeline/editing/SplitElementService';

const ZOOM_MULTIPLIER = 1.5;
const logBase = (n: number) => Math.log(n) / Math.log(ZOOM_MULTIPLIER);

const SCALE_FACTOR = 1000000;
const toDisplayScale = (value: number) => Math.round(value * SCALE_FACTOR);
const toInternalScale = (value: number) => value / SCALE_FACTOR;

function ArrowDownIcon() {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
      <path d="M9 5.5L5 1.5L1 5.5" stroke="var(--accent)" strokeWidth="2" />
    </svg>
  );
}

interface TimelineControlsProps {
  deleteActiveElement: () => Promise<Result<DeleteResultSelectionTarget>>;
  splitActiveTrackElementAtCurrentTime: () => Promise<
    Result<SplitResultSelectionTarget>
  >;
  rippleMode: 'ripple-track' | 'keep-gap';
  setRippleMode: (mode: 'ripple-track' | 'keep-gap') => void;
}

const TimelineControls: React.FC<TimelineControlsProps> = observer(
  ({
    deleteActiveElement,
    splitActiveTrackElementAtCurrentTime,
    rippleMode,
    setRippleMode,
  }) => {
    const videoCreator = useVideoCreatorStore();
    const editorStore = useEditor();
    const layoutStore = useLayoutStore();
    const videoSource = useVideoSource();
    const { activeTracks } = useTimelineTracks();
    const { contentStudioTimelineEnableGapModeRippleEdit } =
      useFlagsCombination(useUserIdentity().currentRole);
    const trackLayerService = useMemo(
      () => new TrackLayerService({ editorStore, videoCreator, videoSource }),
      [editorStore, videoCreator, videoSource],
    );
    const percentToPixel =
      parseFloat(layoutStore.timelineHeight || '0%') *
      0.01 *
      getDocumentHeight();

    const toggleTimeline = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      if (percentToPixel > 40) {
        layoutStore.setTimelineHeight((36 * 100) / getDocumentHeight());
      } else {
        layoutStore.setTimelineHeight(30);
      }
    };

    const onZoomChange = useCallback(
      throttle((value: number) => {
        runInAction(() => {
          videoCreator.timelineScale = value;
        });
      }, 300),
      [],
    );

    const minScale = videoCreator.defaultTimelineScale * 0.2;
    const maxLogScale = logBase(videoCreator.maxTimelineScale / minScale);
    const currLogScale = logBase(videoCreator.timelineScale / minScale);
    const currScale = Math.min(currLogScale, maxLogScale);

    const isSingleTrackSelected = activeTracks.length === 1;

    const handleLayerUp = async (e: React.MouseEvent) => {
      e.stopPropagation();
      const tracks = activeTracks;
      if (tracks.length === 1) {
        const trackId = tracks[0].toString();
        trackLayerService.layerUp(trackId);
      }
    };

    const handleLayerDown = async (e: React.MouseEvent) => {
      e.stopPropagation();
      const tracks = activeTracks;
      if (tracks.length === 1) {
        const trackId = tracks[0].toString();
        trackLayerService.layerDown(trackId);
      }
    };

    return (
      <Main data-testid="timeline-controls">
        <ReframeVideoModal />
        {percentToPixel > 40 && (
          <LeftActions>
            <ZoomControl>
              <Action
                onClick={() => {
                  if (videoCreator.timelineScale <= minScale) return;
                  onZoomChange(
                    Math.max(
                      videoCreator.timelineScale / ZOOM_MULTIPLIER,
                      minScale,
                    ),
                  );
                }}
              >
                <ZoomMinusIcon />
              </Action>
              <PlainSlider
                min={0}
                max={toDisplayScale(maxLogScale)}
                currValue={toDisplayScale(currScale)}
                showInput={false}
                ballColor="var(--text-secondary)"
                ballOpacityDefault={1}
                getValue={(value: number) => {
                  // Convert from logarithmic slider value back to actual scale
                  const actualScale =
                    minScale * ZOOM_MULTIPLIER ** toInternalScale(value);
                  onZoomChange(actualScale);
                }}
                formatTooltip={(value: number) => {
                  const percentage = Math.round(
                    (value / toDisplayScale(maxLogScale)) * 100,
                  );
                  return `${percentage}%`;
                }}
              />
              <Action
                onClick={() => {
                  if (
                    videoCreator.timelineScale >= videoCreator.maxTimelineScale
                  )
                    return;
                  const newScale = Math.min(
                    videoCreator.timelineScale * ZOOM_MULTIPLIER,
                    videoCreator.maxTimelineScale,
                  );
                  // If we're very close to max, just set to max exactly
                  onZoomChange(
                    newScale > videoCreator.maxTimelineScale * 0.95
                      ? videoCreator.maxTimelineScale
                      : newScale,
                  );
                }}
              >
                <ZoomPlusIcon />
              </Action>
            </ZoomControl>
            <Action
              aria-label="cut-track"
              disabled={!isSingleTrackSelected}
              onClick={async e => {
                e.stopPropagation();
                await splitActiveTrackElementAtCurrentTime();
              }}
            >
              <CutIcon
                strokeColor={
                  isSingleTrackSelected
                    ? 'var(--text-secondary)'
                    : 'var(--text-muted)'
                }
              />
              Split
            </Action>
            <Action
              aria-label="layer-up"
              disabled={!isSingleTrackSelected}
              onClick={handleLayerUp}
            >
              <TrackUpIcon
                strokeColor={
                  isSingleTrackSelected
                    ? 'var(--text-secondary)'
                    : 'var(--text-muted)'
                }
              />
              Layer Up
            </Action>
            <Action
              aria-label="layer-down"
              disabled={!isSingleTrackSelected}
              onClick={handleLayerDown}
            >
              <TrackDownIcon
                strokeColor={
                  isSingleTrackSelected
                    ? 'var(--text-secondary)'
                    : 'var(--text-muted)'
                }
              />
              Layer Down
            </Action>
            <Action
              disabled={
                !isSingleTrackSelected && videoCreator.selectedTrack === -1
              }
              onKeyDown={e => {
                if (e.code === 'Space') e.preventDefault();
              }}
              onClick={async e => {
                e.stopPropagation();
                await deleteActiveElement();
              }}
            >
              <DeleteIcon
                strokeColor={
                  !isSingleTrackSelected && videoCreator.selectedTrack === -1
                    ? 'var(--text-muted)'
                    : 'var(--text-secondary)'
                }
              />
              Delete
            </Action>
            {contentStudioTimelineEnableGapModeRippleEdit && (
              <>
                <VerticalSeparator />
                <RippleModeDropdown
                  value={rippleMode}
                  onChange={setRippleMode}
                  disabled={!videoCreator.isVideoCreatorReady}
                />
              </>
            )}
          </LeftActions>
        )}

        <ToggleHide>
          <Action onClick={toggleTimeline}>
            <VisibilityStatus>
              {percentToPixel > 40 ? 'Hide Timeline' : 'Show Timeline'}
            </VisibilityStatus>
            <ArrowDown onClick={toggleTimeline} rotate={percentToPixel > 40}>
              <ArrowDownIcon />
            </ArrowDown>
          </Action>
        </ToggleHide>
      </Main>
    );
  },
);

const Main = styled.div`
  height: 36px;
  padding: 5px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 30px;
`;

const ArrowDown = styled.div<{ rotate: boolean }>`
  transform: ${props => props.rotate && 'rotate(180deg)'};
  display: flex;
  align-items: center;

  /* Doubled selector to out-specify the enclosing Action's blanket
     "svg { width: 14px; height: 14px }". Both rules were .class svg, so source
     order decided and Action won — stretching this 10x6 chevron to 14x14 and
     making it read fatter and larger than the identical chevron on the Ripple
     Mode trigger. Keep the glyph at its natural size so the two match. */
  && svg {
    width: 10px;
    height: 6px;

    path {
      fill: none !important;
    }
  }
`;

const LeftActions = styled.div`
  display: flex;
  gap: 8px;
  display: flex;
  align-items: center;
`;

const Action = styled.button<{ disabled?: boolean }>`
  background-color: transparent;
  outline: 0;
  border: 0;
  border-radius: var(--radius-button);
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${props =>
    props.disabled ? 'var(--text-muted)' : 'var(--text-secondary)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: normal;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
  opacity: ${props => (props.disabled ? 0.7 : 1)};
  padding: 8px;
  transition: all 150ms ease-out;
  svg {
    width: 14px;
    height: 14px;
    path {
      transition: var(--transition-fast);
    }
  }

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
    svg path {
      fill: var(--foreground);
    }
  }

  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }
`;

const VisibilityStatus = styled.span`
  font-size: var(--text-xs);
  line-height: normal;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  pointer-events: none;
  transition: color var(--transition-normal);
`;

const ToggleHide = styled.div`
  margin-left: auto;

  &:hover ${VisibilityStatus} {
    color: var(--text-secondary);
  }
`;

const ZoomControl = styled.div`
  display: flex;
  align-items: center;
  width: 160px;
`;

export default TimelineControls;
